import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, RefreshCw, Shield } from 'lucide-react'
import { api } from '@/services/api'
import { useUserRoles, ROLE_LABELS, type UserRole } from '@/contexts/UserRolesContext'

const ROLE_OPTIONS: UserRole[] = ['admin', 'deployer', 'viewer', 'user']

interface RoleEntry {
  user_email: string
  user_name: string
  role: UserRole
  assigned_by: string
  created_at: string
  updated_at: string
}

export function UserRolesPage() {
  const navigate = useNavigate()
  const { isAdmin } = useUserRoles()
  const [roles, setRoles] = useState<RoleEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newRole, setNewRole] = useState<UserRole>('user')
  const [addError, setAddError] = useState('')

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard', { replace: true }); return }
    fetchRoles()
  }, [isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchRoles() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get('/auth/user-roles/')
      setRoles(res.data)
    } catch {
      setError('Failed to load user roles.')
    } finally {
      setLoading(false)
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    setAddError('')
    if (!newEmail.trim()) { setAddError('Email is required.'); return }

    setSaving(true)
    try {
      await api.post('/auth/user-roles/', {
        user_email: newEmail.trim().toLowerCase(),
        user_name: newName.trim(),
        role: newRole,
      })
      setNewEmail('')
      setNewName('')
      setNewRole('user')
      await fetchRoles()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } }
      setAddError(e?.response?.data?.detail || 'Failed to add role.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(email: string) {
    if (!confirm(`Remove role for ${email}?`)) return
    try {
      await api.delete(`/auth/user-roles/user/${encodeURIComponent(email)}/`)
      setRoles(r => r.filter(x => x.user_email !== email))
    } catch {
      alert('Failed to remove role.')
    }
  }

  async function handleRoleChange(email: string, role: UserRole) {
    try {
      const entry = roles.find(r => r.user_email === email)
      await api.post('/auth/user-roles/', {
        user_email: email,
        user_name: entry?.user_name || '',
        role,
      })
      setRoles(r => r.map(x => x.user_email === email ? { ...x, role } : x))
    } catch {
      alert('Failed to update role.')
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
        <Shield size={24} color="#1E6978" />
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>User Roles</h1>
          <p style={{ margin: '2px 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
            Manage platform access roles for team members.
          </p>
        </div>
        <button
          onClick={fetchRoles}
          style={{ marginLeft: 'auto', background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem' }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Add new role */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: 600 }}>
          <Plus size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Assign Role
        </h2>
        <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '0.75rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>
              Work email *
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="user@aptean.com"
              required
              style={{ width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>
              Display name
            </label>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Full Name"
              style={{ width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>
              Role
            </label>
            <select
              value={newRole}
              onChange={e => setNewRole(e.target.value as UserRole)}
              style={{ padding: '0.5rem 0.625rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', background: '#fff' }}
            >
              {ROLE_OPTIONS.map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={saving}
            style={{ padding: '0.5rem 1rem', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.875rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
          >
            {saving ? 'Saving...' : 'Add Role'}
          </button>
        </form>
        {addError && <p style={{ color: '#dc2626', fontSize: '0.8125rem', margin: '0.5rem 0 0' }}>{addError}</p>}
      </div>

      {/* Role list */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>{error}</div>
        ) : roles.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            No roles assigned yet. Add the first role above.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assigned By</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((entry, i) => (
                <tr key={entry.user_email} style={{ borderBottom: i < roles.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontWeight: 500 }}>{entry.user_name || entry.user_email}</div>
                    {entry.user_name && <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{entry.user_email}</div>}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <select
                      value={entry.role}
                      onChange={e => handleRoleChange(entry.user_email, e.target.value as UserRole)}
                      style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.8125rem', fontFamily: 'inherit', background: '#fff' }}
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', color: '#6b7280' }}>
                    {entry.assigned_by || '—'}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'right' }}>
                    <button
                      onClick={() => handleDelete(entry.user_email)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px 6px', borderRadius: 4 }}
                      title="Remove role"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
